import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";

const PROJECTS = [
  {
    id: "zaunanlagen",
    title: "Zäune & Sichtschutz",
    description: "Doppelstabmatten, Sichtschutz und Tore – auf verschiedenen Grundstücken",
    cover: "/gallery/zaun-abendlicht.webp",
    photos: [
      { src: "/gallery/zaun-abendlicht.webp", caption: "Doppelstabmattenzaun im Abendlicht" },
      { src: "/gallery/zaun-doppelstab.webp", caption: "Doppelstabmattenzaun mit Pforte" },
      { src: "/gallery/sichtschutz-tor.webp", caption: "Sichtschutzzaun mit Hoftor" },
      { src: "/gallery/sichtschutz-anthrazit.jpg", caption: "Sichtschutzzaun in Anthrazit" },
      { src: "/gallery/hoftor-schmiedeeisen.webp", caption: "Hoftor aus Schmiedeeisen" },
      { src: "/gallery/zaun-hang.webp", caption: "Zaunlinie am Hang" },
      { src: "/gallery/zaun-montage.webp", caption: "Zaunmontage im Detail" },
    ],
  },
  {
    id: "neubau-garten",
    title: "Garten-Neuanlage am Neubau",
    description: "Von der Planierung bis zum frisch verlegten Rollrasen",
    cover: "/gallery/rollrasen.webp",
    photos: [
      { src: "/gallery/planierung.webp", caption: "Planierung & Bodenverdichtung" },
      { src: "/gallery/rollrasen.webp", caption: "Frisch verlegter Rollrasen" },
    ],
  },
  {
    id: "garten-gruen",
    title: "Garten & Grünflächen",
    description: "Hecken, Rasen und praktische Lösungen wie Regenwassernutzung",
    cover: "/gallery/hecke-zaun.webp",
    photos: [
      { src: "/gallery/hecke-zaun.webp", caption: "Hecke & Zaun im Grünen" },
      { src: "/gallery/regentank-garten.webp", caption: "Gepflegter Garten mit Regenwassernutzung" },
      { src: "/gallery/regentank-verkleidung.webp", caption: "Regenwassertank mit Sichtschutz-Verkleidung" },
    ],
  },
  {
    id: "erdarbeiten",
    title: "Erdarbeiten",
    description: "Fundamente und Bodenvorbereitung für stabile Zaunanlagen",
    cover: "/gallery/erdbohrer.webp",
    photos: [
      { src: "/gallery/erdbohrer.webp", caption: "Erdbohrungen für Zaunfundamente" },
    ],
  },
];

export default function Gallery() {
  const [open, setOpen] = useState(null); // { project: number, photo: number }

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (dir) =>
      setOpen((o) => {
        if (!o) return null;
        const len = PROJECTS[o.project].photos.length;
        return { project: o.project, photo: (o.photo + dir + len) % len };
      }),
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, step]);

  const current = open ? PROJECTS[open.project] : null;
  const currentPhoto = open ? current.photos[open.photo] : null;

  return (
    <section id="projekte" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-8 lg:px-16 bg-[#F1EFEA]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#244A32]">Projekte</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
            Einblicke in unsere Arbeit
          </h2>
          <p className="mt-3 text-sm text-[#575953]">
            Unsere Projekte nach Objekt sortiert – klicken Sie auf einen Ordner, um alle Fotos dazu zu sehen.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
          {PROJECTS.map((p, i) => (
            <motion.button
              key={p.id}
              type="button"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              onClick={() => setOpen({ project: i, photo: 0 })}
              className="group relative rounded-2xl overflow-hidden h-72 sm:h-80 text-left"
              data-testid={`gallery-project-${p.id}`}
            >
              <img
                src={p.cover}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141614]/85 via-[#141614]/20 to-transparent" />
              <span className="absolute top-4 right-4 flex items-center gap-1.5 bg-white/90 backdrop-blur text-[#1A1C19] text-xs font-bold px-3 py-1.5 rounded-full" data-testid={`gallery-count-${p.id}`}>
                <Images className="w-3.5 h-3.5" />
                {p.photos.length} {p.photos.length === 1 ? "Foto" : "Fotos"}
              </span>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-heading font-semibold text-white text-lg">{p.title}</p>
                <p className="text-sm text-zinc-300 mt-1">{p.description}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {current && currentPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#141614]/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button
              type="button"
              aria-label="Schließen"
              data-testid="gallery-lightbox-close"
              onClick={close}
              className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            {current.photos.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Vorheriges Bild"
                  data-testid="gallery-lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    step(-1);
                  }}
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  aria-label="Nächstes Bild"
                  data-testid="gallery-lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    step(1);
                  }}
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            <motion.div
              key={currentPhoto.src}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={currentPhoto.src}
                alt={currentPhoto.caption}
                className="w-full max-h-[72vh] object-contain rounded-xl"
                data-testid="gallery-lightbox-image"
              />
              <div className="mt-4 text-center">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#C89D66]">{current.title}</span>
                <p className="font-heading font-semibold text-white mt-1" data-testid="gallery-lightbox-title">
                  {currentPhoto.caption}
                </p>
                <p className="text-xs text-zinc-400 mt-1" data-testid="gallery-lightbox-counter">
                  {open.photo + 1} / {current.photos.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
